'use client';

import { ConceptCard } from '@/features/design/concept';
import { AlbumCard } from '@/features/design/history/ui/AlbumCard';
import { EstimateHistoryCard } from '@/features/design/history/ui/EstimateHistoryCard';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { Card, CardContent } from '@/shared/ui/shadcn/card';
import { cn } from '@/shared/lib/cn';
import { useDragScroll } from '@/shared/hooks/use-drag-scroll';
import type { HistoryItem } from '@/features/design/history';
import type { Concept } from '@/types/concept';

interface ConceptGridProps {
  concepts: (HistoryItem | Concept)[];
  isLoading?: boolean;
  className?: string;
  onFavoriteToggle?: (conceptId: string, isFavorite: boolean) => void;
  onDeleted?: () => void;
  emptyMessage?: string;
  layout?: 'grid' | 'carousel';
}

export function ConceptGrid({
  concepts,
  isLoading = false,
  className,
  onFavoriteToggle,
  onDeleted,
  emptyMessage = 'No concepts yet. Create a project and generate your first design!',
  layout = 'grid',
}: ConceptGridProps) {
  const { ref: dragRef, isMouseDown, hasDragged, handlers } = useDragScroll();

  // Carousel card width — sized to match Apple dashboard card dimensions.
  // Mobile: ~1.333 cards visible (peek of next). md/lg: fixed widths matching
  // Apple's 234/249 ladder so visual parity holds with iPhone/iPad/macOS.
  const carouselCardClass =
    'flex-shrink-0 w-[calc((100%-12px)/1.333)] md:w-[282px] lg:w-[332px]';

  // Render skeleton carousel (reused for loading state and empty carousel state)
  const renderCarouselSkeleton = () => (
    <div className="overflow-x-auto overflow-y-hidden -mx-4 px-4 scrollbar-hide">
      <div className="flex gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className={carouselCardClass}>
            <Skeleton className="aspect-[4/3] rounded-lg" />
            <Skeleton className="h-14 mt-0 rounded-b-lg rounded-t-none" />
          </div>
        ))}
      </div>
    </div>
  );

  // Loading state
  if (isLoading) {
    if (layout === 'carousel') {
      return renderCarouselSkeleton();
    }

    return (
      <div
        className={cn(
          'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4',
          className
        )}
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-lg" />
        ))}
      </div>
    );
  }

  // Empty state
  if (concepts.length === 0) {
    // Carousel empty state: render skeleton cards so the section keeps its
    // height and layout (matches Android/Apple dashboard parity).
    if (layout === 'carousel') {
      return renderCarouselSkeleton();
    }

    return (
      <Card className="py-12">
        <CardContent className="text-center">
          <p className="text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  // Carousel layout (for recent items on dashboard)
  if (layout === 'carousel') {
    return (
      <div
        ref={dragRef}
        className={cn(
          'overflow-x-auto overflow-y-hidden -mx-4 px-4 scrollbar-hide select-none',
          isMouseDown ? 'cursor-grabbing' : 'cursor-grab'
        )}
        {...handlers}
      >
        <div className={cn('flex gap-3', hasDragged && 'pointer-events-none')}>
          {concepts.map((concept) => {
            // Check if this is an album/estimate (HistoryItem flags)
            const isAlbum =
              'attributes' in concept &&
              (concept as HistoryItem).attributes?.is_album === true;
            const isEstimate =
              'attributes' in concept &&
              (concept as HistoryItem).attributes?.is_estimate === true;

            // Extract project info based on concept type
            const projectId =
              'relationships' in concept && concept.relationships
                ? concept.relationships.project?.data.id
                : undefined;
            const projectName =
              'relationships' in concept && concept.relationships
                ? (
                    concept.relationships.project as {
                      meta?: { name?: string };
                    }
                  )?.meta?.name
                : undefined;

            return (
              <div key={concept.id} className={carouselCardClass}>
                {isAlbum ? (
                  <AlbumCard
                    item={concept as HistoryItem}
                    showLabel={false}
                  />
                ) : isEstimate ? (
                  <EstimateHistoryCard
                    item={concept as HistoryItem}
                    showLabel={false}
                  />
                ) : (
                  <ConceptCard
                    concept={concept}
                    projectId={projectId}
                    projectName={projectName}
                    onFavoriteToggle={onFavoriteToggle}
                    onDeleted={onDeleted}
                    variant="compact"
                    showLabel={false}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Grid layout (default)
  return (
    <div
      className={cn(
        'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4',
        className
      )}
    >
      {concepts.map((concept) => {
        // Check if this is an album/estimate (HistoryItem flags)
        const isAlbum =
          'attributes' in concept &&
          (concept as HistoryItem).attributes?.is_album === true;
        const isEstimate =
          'attributes' in concept &&
          (concept as HistoryItem).attributes?.is_estimate === true;

        // Extract project info based on concept type
        const projectId =
          'relationships' in concept && concept.relationships
            ? concept.relationships.project?.data.id
            : undefined;
        const projectName =
          'relationships' in concept && concept.relationships
            ? (concept.relationships.project as { meta?: { name?: string } })
                ?.meta?.name
            : undefined;

        return isAlbum ? (
          <AlbumCard key={concept.id} item={concept as HistoryItem} />
        ) : isEstimate ? (
          <EstimateHistoryCard key={concept.id} item={concept as HistoryItem} />
        ) : (
          <ConceptCard
            key={concept.id}
            concept={concept}
            projectId={projectId}
            projectName={projectName}
            onFavoriteToggle={onFavoriteToggle}
            onDeleted={onDeleted}
          />
        );
      })}
    </div>
  );
}
